import apiClient from '../api/client';
import { ECPT_ENDPOINTS } from '../api/endpoints';
import type {
  GetSettingsViewsParams,
  GetSettingsViewsResponse,
  CreateSettingsViewBody,
  CreateSettingsViewResponse,
  UpdateSettingsViewParams,
  UpdateSettingsViewBody,
  UpdateSettingsViewResponse,
  DeleteSettingsViewParams,
  DeleteSettingsViewResponse,
  GetShareUsersParams,
  GetShareUsersResponse,
  ShareViewBody,
  ShareViewResponse,
  GetChecklistTasksResponse,
  CreateChecklistTaskBody,
  CreateChecklistTaskResponse,
  UpdateChecklistTaskBody,
  UpdateChecklistTaskResponse,
  DeleteChecklistTaskResponse,
} from '../../types/api';

export interface CustomField {
  column_id: number;
  column_name: string;
  column_key: string;
  field_level?: 'pem' | 'detail';
  is_custom: boolean;
  plant_code: string;
  created_by: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomFieldsResponse {
  success: boolean;
  message: string;
  data: CustomField[];
}

export interface CustomFieldResponse {
  success: boolean;
  message: string;
  data: CustomField;
}

const ecptSettingsService = {
  /**
   * GET /settings/views
   * Returns all views for the user plus dropdown metadata.
   */
  getViews: async (
    params: GetSettingsViewsParams,
  ): Promise<GetSettingsViewsResponse> => {
    const { data } = await apiClient.get<GetSettingsViewsResponse>(
      ECPT_ENDPOINTS.SETTINGS_VIEWS,
      { params },
    );
    return data;
  },

  /**
   * POST /settings/views
   * Creates a new view and sets it as the default.
   */
  createView: async (
    body: CreateSettingsViewBody,
  ): Promise<CreateSettingsViewResponse> => {
    const { data } = await apiClient.post<CreateSettingsViewResponse>(
      ECPT_ENDPOINTS.SETTINGS_VIEWS,
      body,
    );
    return data;
  },

  /**
   * PUT /settings/views/{view_id}
   * Partially updates an existing view for the authorized user.
   */
  updateView: async (
    viewId: string,
    body: UpdateSettingsViewBody,
    params: UpdateSettingsViewParams,
  ): Promise<UpdateSettingsViewResponse> => {
    const { data } = await apiClient.put<UpdateSettingsViewResponse>(
      ECPT_ENDPOINTS.SETTINGS_VIEW(viewId),
      body,
      { params },
    );
    return data;
  },

  /**
   * DELETE /settings/views/{view_id}
   * Deletes an existing view for the authorized user.
   */
  deleteView: async (
    viewId: string,
    params: DeleteSettingsViewParams,
  ): Promise<DeleteSettingsViewResponse> => {
    const { data } = await apiClient.delete<DeleteSettingsViewResponse>(
      ECPT_ENDPOINTS.SETTINGS_VIEW(viewId),
      { params },
    );
    return data;
  },

  /**
   * GET /settings/share/users
   * Returns the list of users a view can be shared with.
   */
  getShareUsers: async (
    params: GetShareUsersParams,
  ): Promise<GetShareUsersResponse> => {
    const { data } = await apiClient.get<GetShareUsersResponse>(
      ECPT_ENDPOINTS.SETTINGS_SHARE_USERS,
      { params },
    );
    return data;
  },

  /**
   * POST /settings/views/share
   * Shares a view with another user.
   */
  shareView: async (
    body: ShareViewBody,
  ): Promise<ShareViewResponse> => {
    const { data } = await apiClient.post<ShareViewResponse>(
      ECPT_ENDPOINTS.SETTINGS_VIEWS_SHARE,
      body,
    );
    return data;
  },

  getCustomFields: async (plant_code: string, view_id?: string): Promise<CustomFieldsResponse> => {
    const { data } = await apiClient.get<CustomFieldsResponse>(
      ECPT_ENDPOINTS.SETTINGS_CUSTOM_FIELDS,
      { params: { plant_code, ...(view_id ? { view_id } : {}) } },
    );
    return data;
  },

  createCustomField: async (body: {
    column_name: string;
    column_key: string;
    plant_code: string;
    created_by: string;
    field_level: 'pem' | 'detail';
  }): Promise<CustomFieldResponse> => {
    const { data } = await apiClient.post<CustomFieldResponse>(
      ECPT_ENDPOINTS.SETTINGS_CUSTOM_FIELDS,
      body,
    );
    return data;
  },

  updateCustomField: async (
    column_id: number,
    body: { column_name: string },
  ): Promise<CustomFieldResponse> => {
    const { data } = await apiClient.put<CustomFieldResponse>(
      ECPT_ENDPOINTS.SETTINGS_CUSTOM_FIELD(column_id),
      body,
    );
    return data;
  },

  deleteCustomField: async (column_id: number): Promise<void> => {
    await apiClient.delete(ECPT_ENDPOINTS.SETTINGS_CUSTOM_FIELD(column_id));
  },

  /** GET /settings/checklist-tasks */
  getChecklistTasks: async (userId: string): Promise<GetChecklistTasksResponse> => {
    const { data } = await apiClient.get<GetChecklistTasksResponse>(
      ECPT_ENDPOINTS.SETTINGS_CHECKLIST_TASKS,
      { params: { user_id: userId } },
    );
    return data;
  },

  /** POST /settings/checklist-tasks */
  createChecklistTask: async (body: CreateChecklistTaskBody): Promise<CreateChecklistTaskResponse> => {
    const { data } = await apiClient.post<CreateChecklistTaskResponse>(
      ECPT_ENDPOINTS.SETTINGS_CHECKLIST_TASKS,
      body,
    );
    return data;
  },

  /** PUT /settings/checklist-tasks/{task_id} */
  updateChecklistTask: async (
    taskId: string,
    body: UpdateChecklistTaskBody,
    userId: string,
  ): Promise<UpdateChecklistTaskResponse> => {
    const { data } = await apiClient.put<UpdateChecklistTaskResponse>(
      ECPT_ENDPOINTS.SETTINGS_CHECKLIST_TASK(taskId),
      body,
      { params: { user_id: userId } },
    );
    return data;
  },

  /** DELETE /settings/checklist-tasks/{task_id} */
  deleteChecklistTask: async (taskId: string, userId: string): Promise<DeleteChecklistTaskResponse> => {
    const { data } = await apiClient.delete<DeleteChecklistTaskResponse>(
      ECPT_ENDPOINTS.SETTINGS_CHECKLIST_TASK(taskId),
      { params: { user_id: userId } },
    );
    return data;
  },
};

export default ecptSettingsService;
